import api from './api';
import type { AssetCard, TileGradient, SectionAssetCard } from '@/types';

// ─── Shared types ─────────────────────────────────────────────────────────────

export interface TabFeedParams {
  limit?: number;
  offset?: number;
}

export interface TabFeedResponse {
  results: AssetCard[];
  total: number;
  limit: number;
  offset: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Convert milliseconds to "H:MM:SS" or "M:SS" */
export function msToTimeStr(ms: number | null): string {
  if (!ms || ms <= 0) return '0:00';
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const ss = String(s).padStart(2, '0');
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}

/** "2026-04-16T08:30:00Z" → "2h ago", "3d ago", etc. Returns "" if iso is null/empty. */
export function toRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${Math.max(mins, 1)}m ago`;
  const hours = Math.floor(diff / 3_600_000);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(diff / 86_400_000);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

/** "product_launch.mp4" → "product launch" */
export function sanitizeFileName(name: string): string {
  return name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
}

const GRADIENTS: TileGradient[] = ['t1', 't2', 't3', 't4', 't5', 't6', 't7', 't8'];

export function gradientAt(index: number): TileGradient {
  return GRADIENTS[index % GRADIENTS.length];
}

// ─── Transformers ─────────────────────────────────────────────────────────────

import type { MediaItem, PodcastItem, VideoCategory } from '@/types';

/** Map event_type_name from the API to a VideoCategory badge */
function toCategory(eventTypeName: string | null | undefined): VideoCategory | undefined {
  if (!eventTypeName) return undefined;
  const lower = eventTypeName.toLowerCase();
  if (lower.includes('testimonial')) return 'testimonial';
  if (lower.includes('social') || lower.includes('reel') || lower.includes('short')) return 'social';
  if (lower.includes('event') || lower.includes('conference')) return 'event';
  if (lower.includes('internal') || lower.includes('all-hand') || lower.includes('allhand')) return 'internal';
  if (lower.includes('recruit')) return 'recruit';
  if (lower.includes('training') || lower.includes('learning') || lower.includes('onboard')) return 'training';
  if (lower.includes('highlight') || lower.includes('reel')) return 'highlight';
  return undefined;
}

export function assetToMediaItem(asset: AssetCard, index: number): MediaItem {
  return {
    id: asset.asset_id,
    title: sanitizeFileName(asset.file_name),
    duration: asset.duration_ms != null ? msToTimeStr(asset.duration_ms) : '',
    gradient: gradientAt(index),
    tags: asset.event_type_name ? [asset.event_type_name] : [],
    meta: toRelativeTime(asset.source_created_at) || 'Just added',
    badge: 'new' as const,
    thumbnail: asset.thumbnail_url ?? undefined,
    storage_url: asset.storage_url ?? undefined,
    category: toCategory(asset.event_type_name),
    timestamp: toRelativeTime(asset.source_created_at) || undefined,
    contentType: asset.content_type,
  };
}

/** Same as assetToMediaItem, plus the search-only match fields so search
 * result cards can render as a regular MediaTile with match info layered on. */
export function assetToSearchMediaItem(asset: AssetCard, index: number): MediaItem {
  return {
    ...assetToMediaItem(asset, index),
    badge: undefined,
    matchLabel: asset.match_label,
    matchScore: asset.match_score,
    matchedIn: asset.matched_in,
    matchedEntity: asset.matched_entity,
  };
}

export function assetToTrendingMediaItem(asset: AssetCard & { total_views?: number }, index: number): MediaItem {
  return {
    id: asset.asset_id,
    title: sanitizeFileName(asset.file_name),
    duration: asset.duration_ms != null ? msToTimeStr(asset.duration_ms) : '',
    gradient: gradientAt(index),
    tags: asset.event_type_name ? [asset.event_type_name] : [],
    meta: asset.total_views != null ? `${asset.total_views.toLocaleString()} view${asset.total_views !== 1 ? 's' : ''}` : 'Trending',
    badge: (asset.total_views != null && asset.total_views > 0) ? 'hot' as const : undefined,
    thumbnail: asset.thumbnail_url ?? undefined,
    storage_url: asset.storage_url ?? undefined,
    category: toCategory(asset.event_type_name),
    contentType: asset.content_type,
  };
}

export function assetToPodcastItem(asset: AssetCard, _index: number): PodcastItem {
  return {
    id: asset.asset_id,
    title: sanitizeFileName(asset.file_name),
    speaker: asset.project_name ?? asset.source_system,
    duration: asset.duration_ms != null ? msToTimeStr(asset.duration_ms) : '',
    tags: asset.event_type_name ? [asset.event_type_name] : ['podcast'],
    meta: toRelativeTime(asset.source_created_at) || 'Recently added',
    thumbnail: asset.thumbnail_url ?? undefined,
    storage_url: asset.storage_url ?? undefined,
  };
}

/** Maps a v3 SectionAssetCard → MediaItem */
export function sectionAssetToMediaItem(asset: SectionAssetCard, index: number): MediaItem {
  return {
    id: asset.asset_id,
    title: sanitizeFileName(asset.file_name),
    duration: asset.duration_ms != null ? msToTimeStr(asset.duration_ms) : '',
    gradient: gradientAt(index),
    tags: [],
    meta: toRelativeTime(asset.source_created_at) || 'Recently added',
    thumbnail: asset.thumbnail_url ?? undefined,
    storage_url: asset.storage_url ?? undefined,
    download_url: asset.download_url ?? undefined,
    timestamp: toRelativeTime(asset.source_created_at) || undefined,
    contentType: asset.content_type,
  };
}

/** Maps a v3 SectionAssetCard → PodcastItem */
export function sectionAssetToPodcastItem(asset: SectionAssetCard, _index: number): PodcastItem {
  return {
    id: asset.asset_id,
    title: sanitizeFileName(asset.file_name),
    speaker: '',
    duration: asset.duration_ms != null ? msToTimeStr(asset.duration_ms) : '',
    tags: [],
    meta: toRelativeTime(asset.source_created_at) || 'Recently added',
    thumbnail: asset.thumbnail_url ?? undefined,
    storage_url: undefined,
  };
}

// ─── API calls ────────────────────────────────────────────────────────────────

export const tabFeedService = {
  /** GET /api/v2/sections/new — assets from the last 7 days, newest first */
  getNew: (params: TabFeedParams = {}) =>
    api.get<TabFeedResponse>('/sections/new', {
      params: { limit: 15, offset: 0, ...params },
    }),

  /** GET /api/v2/sections/trending — most-viewed assets in the org */
  getTrending: (params: TabFeedParams = {}) =>
    api.get<TabFeedResponse>('/sections/trending', {
      params: { limit: 15, offset: 0, ...params },
    }),

  /** GET /api/v2/sections/podcast — videos from podcast-type events, newest first */
  getPodcast: (params: TabFeedParams = {}) =>
    api.get<TabFeedResponse>('/sections/podcast', {
      params: { limit: 15, offset: 0, ...params },
    }),

  /** GET /api/v2/sections/testimonial — customer testimonial videos */
  getTestimonial: (params: TabFeedParams = {}) =>
    api.get<TabFeedResponse>('/sections/testimonial', {
      params: { limit: 15, offset: 0, ...params },
    }),

  /** GET /api/v2/sections/social-media-shorts — short-form social content */
  getSocialMediaShorts: (params: TabFeedParams = {}) =>
    api.get<TabFeedResponse>('/sections/social-media-shorts', {
      params: { limit: 15, offset: 0, ...params },
    }),
};
